import { AssistantStore } from '@/ai/store/AssistantStore'
import { UUIDGeneratorBrowser } from '@/lib/utils/browser'
import { AES, HmacMD5 } from '@/lib/crypto/crypto'
import { ChatStreamPayload } from '@/ai/model/ChatStreamPayload'
import ChatStreamPayloadType from '@/ai/model/ChatStreamPayloadType'
import { AssistantApi } from '@/ai/api/AssistantApi'
import AiChatMessageStatus from '@/ai/model/AiChatMessageStatus'

const authorizationBearerTokenKey = HmacMD5.hash('authBearerToken')
let socket = null
let reconnectTimer = null

const AssistantSocket = {
  userId   : null,
  accountId: null,
  sessionId: null,

  manualClose         : false,
  reconnectAttempts   : 0,
  maxReconnectAttempts: 6,

  connect (userId, accountId, sessionId) {
    this.userId = userId
    this.accountId = accountId
    this.sessionId = sessionId || UUIDGeneratorBrowser()
    this.manualClose = false
    this.reconnectAttempts = 0
    this.clearReconnectTimer()
    this.createConnection()
  },

  createConnection () {
    if (!this.userId || !this.accountId || !this.sessionId) {
      return
    }

    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      return
    }

    const wsUrl = `${window.Vue?.VUE_APP_WEBSOCKET_URL}${process.env.VUE_APP_WEBSOCKET_AI_ASSISTANT_CHAT_PATH}/${this.accountId}/${this.userId}/${this.sessionId}`
    const bearerToken = window.Vue && AES.decrypt(window.Vue.$sessionStorage.get(authorizationBearerTokenKey, null)) || null
    const customHeaderString = `quarkus-http-upgrade#Authorization#Bearer ${bearerToken}`
    const encodedHeaderProtocol = encodeURIComponent(customHeaderString)

    socket = new WebSocket(wsUrl, [
      'bearer-token-carrier',
      encodedHeaderProtocol
    ])

    const currentSocket = socket

    currentSocket.onopen = async () => {
      if (currentSocket !== socket) {
        return
      }
      const wasReconnect = this.reconnectAttempts > 0
      // eslint-disable-next-line no-console
      console.log('Connection opened')
      AssistantStore.Socket.Connected = true
      this.reconnectAttempts = 0
      this.clearReconnectTimer()
      if (wasReconnect) {
        await this.refreshSession()
      }
    }

    currentSocket.onclose = () => {
      // eslint-disable-next-line no-console
      console.log('Connection closed')
      if (currentSocket !== socket) {
        return
      }
      socket = null
      AssistantStore.Socket.Connected = false
      AssistantStore.Streaming.Active = false
      AssistantStore.Streaming.AnimationActive = false
      AssistantStore.Messages.SendEnabled = true
      AssistantStore.Streaming.DisplayedContent = ''
      AssistantStore.Streaming.StreamId = null
      AssistantStore.Editing.Active = false

      if (!this.manualClose) {
        this.scheduleReconnect()
      }
    }

    currentSocket.onerror = (error) => {
      // eslint-disable-next-line no-console
      console.error('Assistant websocket error', error)

      if (currentSocket.readyState !== WebSocket.CLOSED && currentSocket.readyState !== WebSocket.CLOSING) {
        currentSocket.close()
      }
    }

    currentSocket.onmessage = (event) => {
      this.handleMessage(event)
    }
  },

  scheduleReconnect () {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return
    }

    const delay = Math.min(500 * (2 ** this.reconnectAttempts), 5000)
    this.reconnectAttempts += 1
    this.clearReconnectTimer()

    reconnectTimer = setTimeout(() => {
      this.createConnection()
    }, delay)
  },

  clearReconnectTimer () {
    if (!reconnectTimer) {
      return
    }

    clearTimeout(reconnectTimer)
    reconnectTimer = null
  },

  async handleMessage (event) {
    const payload = new ChatStreamPayload(JSON.parse(event.data))
    if (payload.type === ChatStreamPayloadType.STOP_FINISHED) {
      AssistantStore.Streaming.StopRequested = false
      AssistantStore.Messages.SendEnabled = true
      AssistantStore.Streaming.Active = false
      AssistantStore.Streaming.AnimationActive = false

      const messages = AssistantStore.Messages.Data
      const lastMessage = messages[messages.length - 1]
      if (lastMessage && lastMessage.role === 'AI') {
        lastMessage.status = AiChatMessageStatus.STOPPED
      } else {
        messages.push({
          role    : 'AI',
          content : '',
          status  : AiChatMessageStatus.STOPPED,
          streamId: payload.streamId
        })
      }
      return
    }
    if (payload.type === ChatStreamPayloadType.TOOL_EXECUTION) {
      // eslint-disable-next-line no-console
      console.log('Tool execution payload received:', payload)
      return
    }
    if (payload.type === ChatStreamPayloadType.TITLE_UPDATE) {
      await this.refreshRecentChatSessions()
      return
    }
    if (payload.type === ChatStreamPayloadType.ERROR) {
      await this.refreshSession()
      return
    }
    if (payload.type === ChatStreamPayloadType.COMPLETED_MESSAGE) {
      AssistantStore.Streaming.Active = false
      AssistantStore.Streaming.StopRequested = false
      if (!AssistantStore.Streaming.AnimationActive) {
        AssistantStore.Messages.SendEnabled = true
      }
      return
    }
    if (AssistantStore.Streaming.StopRequested) {
      return
    }
    AssistantStore.Messages.SendEnabled = false
    this.appendChunk(payload)
  },

  appendChunk (chatStreamPayload) {
    const payloadStreamId = chatStreamPayload.streamId
    if (payloadStreamId && payloadStreamId !== AssistantStore.Streaming.StreamId) {
      return
    }
    const messages = AssistantStore.Messages.Data
    const lastMessage = messages[messages.length - 1]

    if (chatStreamPayload.type === ChatStreamPayloadType.PARTIAL_MESSAGE) {
      const content = chatStreamPayload.content || ''

      if (lastMessage && lastMessage.role === 'AI') {
        lastMessage.content += content
      } else {
        messages.push({
          role  : 'AI',
          content,
          status: AiChatMessageStatus.COMPLETED
        })
      }
    }
  },

  send (text) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      AssistantStore.Streaming.StopRequested = false
      socket.send(text)
      AssistantStore.Streaming.Active = true
    }
  },

  stopStreaming (userInput) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(userInput))
    }
  },

  close () {
    this.manualClose = true
    this.clearReconnectTimer()
    this.reconnectAttempts = 0

    AssistantStore.Streaming.Active = false
    AssistantStore.Messages.SendEnabled = true
    AssistantStore.Socket.Connected = false
    AssistantStore.Streaming.DisplayedContent = ''
    AssistantStore.Streaming.StreamId = null
    AssistantStore.Streaming.AnimationActive = false
    AssistantStore.Editing.Active = false
    if (socket) {
      socket.close()
    }
  },

  async refreshRecentChatSessions () {
    AssistantStore.Sessions.Recent.Data = await AssistantApi.getChatSessions(this.accountId, this.userId, 5)
    AssistantStore.SelectedSession.Data = AssistantStore.Sessions.Recent.Data.find(
      (session) => session.sessionId === this.sessionId
    ) || null
  },

  async refreshSession () {
    AssistantStore.Sessions.Recent.Loading = false
    AssistantStore.Sessions.All.Loading = false
    AssistantStore.Greeting.Loading = false
    AssistantStore.Suggestions.Loading = false
    AssistantStore.Messages.Data.splice(0)
    AssistantStore.Messages.Loading = false
    AssistantStore.Messages.SendEnabled = true
    AssistantStore.Streaming.Active = false
    AssistantStore.Streaming.DisplayedContent = ''
    AssistantStore.Streaming.StreamId = null
    AssistantStore.Streaming.AnimationActive = false
    AssistantStore.Editing.Active = false
    AssistantStore.Messages.Data = await AssistantApi.getChatMessages(this.accountId, this.sessionId)
  }
}

export default AssistantSocket
